const ACTION_RESULT_OUTCOMES = [
  'succeeded',
  'partial',
  'conflict',
  'forbidden',
  'indeterminate',
  'failed',
  'refused',
  'not_run',
] as const;

/** How an action ended, from `ActionResult.outcome`. */
export type ActionResultOutcome = (typeof ACTION_RESULT_OUTCOMES)[number];

/** The `actionResult` arm of a stream frame. */
export type StreamActionResult = {
  /** Empty when the action was refused or not run. */
  action: string;
  outcome: ActionResultOutcome;
  reasons: ReadonlyArray<string>;
  /** The permissions a `forbidden` fleet call requires. */
  permissions: ReadonlyArray<string>;
  message: string;
  /** HTTPS static map image of the action's geometry. */
  mapUrl?: string;
  /** Web-app path of the record the action created or changed, with no host. */
  detailsUrl?: string;
};

// One leading slash keeps the path on our own origin; `//host` and `/\host`
// would leave it.
const APP_PATH_PATTERN = /^\/(?![/\\])[^\s\\]*$/;

const isHttpsUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const isAppPath = (value: unknown): value is string =>
  typeof value === 'string' && APP_PATH_PATTERN.test(value);

const isActionResultOutcome = (value: unknown): value is ActionResultOutcome =>
  typeof value === 'string' &&
  (ACTION_RESULT_OUTCOMES as ReadonlyArray<string>).includes(value);

const stringsFromUnknown = (value: unknown): ReadonlyArray<string> =>
  Array.isArray(value)
    ? value.filter(
        (entry): entry is string => typeof entry === 'string' && entry !== ''
      )
    : [];

/**
 * Reads the `actionResult` arm of a stream frame. Relay frames can carry the
 * arm with empty defaults, so only a known outcome counts as a result. A map
 * that is not HTTPS and a details URL that is not a path on our own origin
 * are dropped.
 * @param raw - The wire `actionResult` value.
 * @returns The action result, or `null` when the arm is not populated.
 */
export const parseWireActionResult = (
  raw: unknown
): StreamActionResult | null => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const { action, outcome, reasons, permissions, message, mapUrl, detailsUrl } =
    raw as Record<string, unknown>;
  if (!isActionResultOutcome(outcome)) {
    return null;
  }
  return {
    action: typeof action === 'string' ? action : '',
    outcome,
    reasons: stringsFromUnknown(reasons),
    permissions: stringsFromUnknown(permissions),
    message: typeof message === 'string' ? message : '',
    ...(isHttpsUrl(mapUrl) ? { mapUrl } : {}),
    ...(isAppPath(detailsUrl) ? { detailsUrl } : {}),
  };
};
